import { skipToken } from "@reduxjs/toolkit/query";
import { Download } from "lucide-react";
import React, { useEffect, useState } from "react";
import SimulationHeading from "./simulationHeading";
import { useAppDispatch } from "../../../../app/hooks";
import { useListScenariosQuery, useSaveScenarioMutation } from "../../../../api/scenarioApi";
import useCurrentPlan from "../../../../hooks/useCurrentPlan";
import type { ListScenariosRequest } from "../../../../contracts";
import StateIndicator from "../../components/atoms/stateIndicator";
import ActionButton from "../../components/atoms/actionButton";
import { notify } from "../../../../components/notify";
import SaveScenarioWindow from "../../scenarios/components/saveScenarioWindow";

interface SimulationToolbarProps {
    onExport: () => void;
    onShowSimulatorInput: () => void;
}

const SimulationToolbar: React.FC<SimulationToolbarProps> = ({
    onExport
}) => {
    const dispatch = useAppDispatch();
    const [saveScenario, { isLoading: saveScenarioIsLoading }] = useSaveScenarioMutation();
    const { chatConversationId, chatConversationMessageId, brandId, subBrandId, headingData, currentPlan } = useCurrentPlan();
    const [saveScenarioWindowVisible, setSaveScenarioWindowVisible] = useState<boolean>(false);

    const listRequest: ListScenariosRequest | typeof skipToken = chatConversationId != null && chatConversationMessageId != null
        ? {
                take: 9999,
                filter: {
                    logic: "and",
                    filters: [
                        { field: "chatconversationid", operator: "eq", value: chatConversationId },
                        { field: "chatconversationmessageid", operator: "eq", value: chatConversationMessageId }
                    ]
                }
            }
        : skipToken;

    const {
        currentData: scenarioResponse,
        error: scenarioError,
        isFetching: scenarioIsFetching,
        refetch
    } = useListScenariosQuery(listRequest);

    const scenarios = scenarioResponse?.data;
    const scenarioSaved = scenarios != null && scenarios.length > 0;

    useEffect((): void => {
        if (!scenarioError) {
            return;
        }

        if ("status" in scenarioError && scenarioError.status === 404) {
            return;
        }

        dispatch(notify("An error occurred attempting to retrieve scenario.", "error"));
    }, [dispatch, scenarioError]);

    const buttonsDisabled = !currentPlan;
    const saveScenarioDisabled = buttonsDisabled
        || !chatConversationId
        || !chatConversationMessageId
        || !brandId
        || !subBrandId
        || scenarioIsFetching
        || saveScenarioIsLoading
        || scenarioSaved;

    const compareScenarioDisabled = buttonsDisabled
        || !chatConversationId
        || !chatConversationMessageId
        || scenarioIsFetching
        || saveScenarioIsLoading
        || scenarioSaved;

    const handleExportClick = (): void => {
        onExport();
    };

    const handleSaveScenarioClick = (): void => {
        setSaveScenarioWindowVisible(true);
    };

    const handleCompareScenarioClick = (): void => {
        // setSaveScenarioWindowVisible(true);
    };

    return (
        <>
            {saveScenarioWindowVisible && (
                <SaveScenarioWindow
                    scenarioDescription={[
                        headingData?.objective,
                        headingData?.skuName != null ? `for ${headingData.skuName}` : null,
                        headingData?.retailers ? `at ${headingData.retailers}` : null
                    ].filter(Boolean).join(" ")}
                    onClose={() => setSaveScenarioWindowVisible(false)}
                    onSave={async (name, description) => {
                        if (!chatConversationId || !chatConversationMessageId || !brandId || !subBrandId) {
                            return;
                        }

                        try {
                            await saveScenario({
                                name,
                                description,
                                chat_conversation_id: chatConversationId,
                                chat_conversation_message_id: chatConversationMessageId,
                                brand_id: brandId,
                                sub_brand_id: subBrandId
                            }).unwrap();

                            setSaveScenarioWindowVisible(false);
                            await refetch();
                        } catch {
                            dispatch(notify("An error occurred attempting to save the scenario.", "error"));
                        }
                    }}
                />
            )}
            <div className="w-100 mb-1">
                <h1 className="fw-semibold simulator-title k-text-highlight-primary">Simulator</h1>
                <SimulationHeading />
                <div className="w-100 py-3 d-flex flex-wrap align-items-center justify-content-between gap-3 min-w-0">
                    <div className="d-flex align-items-center gap-3 min-w-0">
                        <span
                            className="bg-success rounded-pill flex-shrink-0"
                            style={{ width: "4px", minHeight: "24px" }}
                            aria-hidden="true"
                        />

                        <h2 className="small fw-bold mb-0 text-nowrap">AI Generated Plan</h2>
                        <StateIndicator isSuccessful={scenarioSaved} successText="Saved" warningText="Unsaved Scenario" />
                    </div>

                    <div className="d-flex align-items-center justify-content-end gap-2 flex-wrap">
                        {/* <Button */}
                        {/*    type="button" */}
                        {/*    size="small" */}
                        {/*    fillMode="flat" */}
                        {/*    svgIcon={slidersIcon} */}
                        {/*    onClick={onShowSimulatorInput} */}
                        {/*    title="Simulation Inputs" */}
                        {/*    disabled={buttonsDisabled || !hasLatestToolMessage} */}
                        {/* /> */}

                        <ActionButton variant="tertiary" endIcon={<Download size={14} />} onClick={handleExportClick} disabled={buttonsDisabled}>
                            Export
                        </ActionButton>

                        {/* <ActionButton variant="tertiary" endIcon={<Share2 size={14} />} onClick={handleShareClick} disabled={buttonsDisabled}>
                            Share
                        </ActionButton> */}

                        <ActionButton variant="secondary" onClick={handleCompareScenarioClick} disabled={compareScenarioDisabled}>
                            Compare Scenario
                        </ActionButton>

                        <ActionButton variant="primary" onClick={handleSaveScenarioClick} disabled={saveScenarioDisabled}>
                            Save Scenario
                        </ActionButton>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SimulationToolbar;
